<template>
  <v-container fluid class="pa-4">
    <!-- Page Header -->
    <v-row class="mb-6">
      <v-col cols="12">
        <div class="d-flex align-center">
          <v-avatar color="primary" size="48" class="mr-4">
            <v-icon color="white" size="24">mdi-shield-account</v-icon>
          </v-avatar>
          <div>
            <h1 class="text-h4 font-weight-bold text--primary mb-1">
              {{ $t('role.dashboard.title') }}
            </h1>
            <p class="text--secondary text-subtitle-1 mb-0">
              {{ $t('role.dashboard.overview') }}
            </p>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Statistics Cards -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="3" v-for="stat in statistics" :key="stat.title">
        <v-card
          :color="stat.color"
          dark
          class="stat-card elevation-4"
          :class="{ 'stat-card--animated': stat.animated }"
        >
          <v-card-text class="pa-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h3 font-weight-bold mb-1">{{ stat.value }}</div>
                <div class="text-subtitle-1 opacity-90">{{ stat.title }}</div>
                <div v-if="stat.subtitle" class="text-caption opacity-75 mt-1">
                  {{ stat.subtitle }}
                </div>
              </div>
              <v-avatar :color="stat.iconColor" size="64" class="elevation-2">
                <v-icon size="32">{{ stat.icon }}</v-icon>
              </v-avatar>
            </div>
          </v-card-text>
          <v-card-actions v-if="stat.action" class="pa-2">
            <v-btn
              :to="stat.action.to"
              text
              small
              class="text-capitalize"
              v-can="stat.action.permission"
            >
              {{ stat.action.text }}
              <v-icon right small>mdi-arrow-right</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Quick Actions -->
    <v-row class="mb-6">
      <v-col cols="12">
        <v-card class="elevation-2">
          <v-card-title class="pa-4">
            <v-icon color="primary" class="mr-2">mdi-lightning-bolt</v-icon>
            {{ $t('role.dashboard.quick_actions') }}
          </v-card-title>
          <v-card-text class="pa-4 pt-0">
            <v-row>
              <v-col
                cols="12"
                sm="6"
                md="3"
                v-for="action in quickActions"
                :key="action.title"
              >
                <v-card
                  :to="action.to"
                  hover
                  class="action-card elevation-1"
                  v-can="action.permission"
                >
                  <v-card-text class="text-center pa-4">
                    <v-avatar :color="action.color" size="48" class="mb-3">
                      <v-icon color="white" size="24">{{ action.icon }}</v-icon>
                    </v-avatar>
                    <div class="text-subtitle-1 font-weight-medium">{{ action.title }}</div>
                    <div class="text-caption text--secondary">{{ action.description }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Content Grid -->
    <v-row>
      <!-- Recent Roles -->
      <v-col cols="12" lg="6" class="mb-4">
        <v-card class="elevation-2 h-100">
          <v-card-title class="pa-4">
            <v-icon color="primary" class="mr-2">mdi-account-group</v-icon>
            {{ $t('role.dashboard.recent_roles') }}
            <v-spacer></v-spacer>
            <v-btn
              :to="{ name: 'roles' }"
              text
              small
              color="primary"
              v-can="'view roles'"
            >
              {{ $t('button.view') }}
              <v-icon right small>mdi-arrow-right</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-4 pt-0">
            <div v-if="recentRoles.length === 0" class="text-center py-8">
              <v-icon color="grey lighten-1" size="64">mdi-information-outline</v-icon>
              <p class="text--secondary mt-2">{{ $t('role.dashboard.no_roles') }}</p>
            </div>
            <v-list v-else class="pa-0">
              <v-list-item
                v-for="role in recentRoles"
                :key="role.id"
                class="px-0 mb-2"
              >
                <v-list-item-avatar color="primary" size="40">
                  <v-icon color="white" size="20">mdi-shield</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-medium">
                    {{ role.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text--secondary">
                    {{ role.description || 'No description' }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip
                    :color="getGuardColor(role.guard_name)"
                    small
                    outlined
                  >
                    {{ role.guard_name }}
                  </v-chip>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Permission Categories -->
      <v-col cols="12" lg="6" class="mb-4">
        <v-card class="elevation-2 h-100">
          <v-card-title class="pa-4">
            <v-icon color="primary" class="mr-2">mdi-security-network</v-icon>
            {{ $t('role.dashboard.permission_categories') }}
            <v-spacer></v-spacer>
            <v-btn
              :to="{ name: 'permissions' }"
              text
              small
              color="primary"
              v-can="'view permissions'"
            >
              {{ $t('button.view') }}
              <v-icon right small>mdi-arrow-right</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-4 pt-0">
            <div v-if="permissionCategories.length === 0" class="text-center py-8">
              <v-icon color="grey lighten-1" size="64">mdi-information-outline</v-icon>
              <p class="text--secondary mt-2">{{ $t('role.dashboard.no_permissions') }}</p>
            </div>
            <div v-else>
              <div
                v-for="category in permissionCategories"
                :key="category.name"
                class="mb-4"
              >
                <div class="d-flex align-center justify-space-between mb-2">
                  <span class="text-subtitle-1 font-weight-medium">{{ category.name }}</span>
                  <v-chip color="primary" small outlined>
                    {{ category.count }}
                  </v-chip>
                </div>
                <v-progress-linear
                  :value="(category.count / permissions.length) * 100"
                  :color="getCategoryColor(category.name)"
                  height="6"
                  rounded
                ></v-progress-linear>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Role Distribution Chart -->
    <v-row v-if="roles.length > 0">
      <v-col cols="12">
        <v-card class="elevation-2">
          <v-card-title class="pa-4">
            <v-icon color="primary" class="mr-2">mdi-chart-pie</v-icon>
            {{ $t('role.dashboard.role_distribution') }}
          </v-card-title>
          <v-card-text class="pa-4">
            <v-row>
              <v-col
                cols="12"
                sm="6"
                md="4"
                lg="3"
                v-for="role in roles.slice(0, 8)"
                :key="role.id"
              >
                <div class="text-center">
                  <v-progress-circular
                    :value="(role.users_count || 0) / Math.max(users.length, 1) * 100"
                    :color="getRoleColor(role.name)"
                    size="80"
                    width="8"
                    class="mb-2"
                  >
                    <span class="text-caption font-weight-bold">
                      {{ Math.round((role.users_count || 0) / Math.max(users.length, 1) * 100) }}%
                    </span>
                  </v-progress-circular>
                  <div class="text-subtitle-2 font-weight-medium">{{ role.name }}</div>
                  <div class="text-caption text--secondary">
                    {{ role.users_count || 0 }} {{ $t('role.label.users') }}
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  name: 'RoleDashboard',
  data () {
    return {
      roles: [],
      permissions: [],
      users: [],
      loading: false
    }
  },
  computed: {
    statistics () {
      return [
        {
          title: this.$t('role.dashboard.total_roles'),
          value: this.roles.length,
          color: 'primary',
          iconColor: 'primary',
          icon: 'mdi-shield-account',
          animated: true,
          action: {
            text: this.$t('button.view'),
            to: { name: 'roles' },
            permission: 'view roles'
          }
        },
        {
          title: this.$t('role.dashboard.total_permissions'),
          value: this.permissions.length,
          color: 'success',
          iconColor: 'success',
          icon: 'mdi-security-network',
          animated: true,
          action: {
            text: this.$t('button.view'),
            to: { name: 'permissions' },
            permission: 'view permissions'
          }
        },
        {
          title: this.$t('role.dashboard.total_users'),
          value: this.users.length,
          color: 'info',
          iconColor: 'info',
          icon: 'mdi-account-group',
          animated: true,
          action: {
            text: this.$t('button.view'),
            to: { name: 'users' },
            permission: 'view users'
          }
        },
        {
          title: this.$t('role.dashboard.active_users'),
          value: this.activeUsers,
          subtitle: `${Math.round((this.activeUsers / Math.max(this.users.length, 1)) * 100)}% ${this.$t('role.dashboard.of_total')}`,
          color: 'warning',
          iconColor: 'warning',
          icon: 'mdi-account-check',
          animated: true
        }
      ]
    },
    quickActions () {
      return [
        {
          title: this.$t('role.button.create'),
          description: this.$t('role.dashboard.create_new_role'),
          icon: 'mdi-plus',
          color: 'primary',
          to: { name: 'create-role' },
          permission: 'create roles'
        },
        {
          title: this.$t('role.button.all'),
          description: this.$t('role.dashboard.view_all_roles'),
          icon: 'mdi-format-list-bulleted',
          color: 'success',
          to: { name: 'roles' },
          permission: 'view roles'
        },
        {
          title: this.$t('permission.button.all'),
          description: this.$t('role.dashboard.manage_permissions'),
          icon: 'mdi-security-network',
          color: 'warning',
          to: { name: 'permissions' },
          permission: 'view permissions'
        },
        {
          title: this.$t('role.button.user_role_manager'),
          description: this.$t('role.dashboard.assign_user_roles'),
          icon: 'mdi-account-switch',
          color: 'info',
          to: { name: 'user-role-manager' },
          permission: 'assign roles'
        }
      ]
    },
    activeUsers () {
      return this.users.filter(user => user.active).length
    },
    recentRoles () {
      return this.roles.slice(0, 5)
    },
    permissionCategories () {
      const categories = {}
      this.permissions.forEach(permission => {
        const category = permission.name.split(' ')[0] // Get first word as category
        if (!categories[category]) {
          categories[category] = 0
        }
        categories[category]++
      })

      return Object.keys(categories).map(name => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        count: categories[name]
      })).sort((a, b) => b.count - a.count)
    }
  },
  methods: {
    async loadData () {
      try {
        await this.withLoading(async () => {
          const [rolesData, permissionsData, usersData] = await Promise.all([
            this.$roleService.getRoles(),
            this.$permissionService.getPermissions(),
            this.$userService.getUsers()
          ])

          this.roles = rolesData
          this.permissions = permissionsData
          this.users = usersData
        }, 'loading')
      } catch (error) {
        console.log('Component caught error in loadData:', error)
      }
    },
    getGuardColor (guard) {
      const colors = {
        api: 'primary',
        web: 'secondary',
        admin: 'success'
      }
      return colors[guard] || 'grey'
    },
    getCategoryColor (category) {
      const colors = ['primary', 'success', 'warning', 'info', 'error', 'purple', 'pink', 'indigo']
      const index = category.toLowerCase().charCodeAt(0) % colors.length
      return colors[index]
    },
    getRoleColor (roleName) {
      const colors = ['primary', 'success', 'warning', 'info', 'error', 'purple', 'pink', 'indigo']
      const index = roleName.toLowerCase().charCodeAt(0) % colors.length
      return colors[index]
    }
  },
  async mounted () {
    this.$emit('parent-title', this.$t('role.dashboard.title'))
    await this.loadData()
  }
}
</script>

<style scoped>
.stat-card {
  transition: all 0.3s ease;
  border-radius: 16px;
}

.stat-card--animated {
  animation: pulse 2s infinite;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

.action-card {
  transition: all 0.3s ease;
  border-radius: 12px;
  cursor: pointer;
}

.action-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1) !important;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(25, 118, 210, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
}

.h-100 {
  height: 100%;
}

.v-card {
  border-radius: 12px;
}

.v-list-item {
  border-radius: 8px;
  transition: all 0.2s ease;
}

.v-list-item:hover {
  background-color: rgba(25, 118, 210, 0.04);
}

.v-progress-circular {
  transition: all 0.3s ease;
}

.v-progress-linear {
  border-radius: 3px;
}
</style>
